import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import Modal from '@material-ui/core/Modal';
import { Grid, Typography, List, ListItem, TextField } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import postOnSlack from 'src/core/postOnSlack';
import {
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
  Link,
  Box,
  ButtonGroup,
  Button,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
} from '@mui/material';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import {
  COLOR_BLACK,
  COLOR_RED,
  COLOR_BLUE_BLACK_LIGHT,
  COLOR_GREY,
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_WHITE,
  ProductIDOptions,
  COLOR_TOOLTIP_BACKGROUND,
  PAYMENT_MODAL_GREEN,
  PAYMENT_MODAL_GREEN_HOVER,
  countryListWithCode,
  activeListingAsPerCountryTier,
  CountryCodeWithCurrency,
  CurrencyWithSubscriptionPrice,
  exchangeRates,
  countryCodeToNumberOfListingsCountryPlan,
} from 'src/const';
import { Payment } from '../HOC/Payment';
import { SubscriptionModalPropTypes } from 'src/interfaces/iproptypes';
import { formatCurrency, formatCurrencyBasedOnCountryCode, getCountryDataFromCountryCode } from 'src/utils/functions';
import { InfoTooltip } from '../HOC/Tooltips';
import { callApi } from 'src/core/callApi';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DoneRoundedIcon from '@mui/icons-material/DoneRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { COLOR_SUCCESS_1, COLOR_SUCCESS_BACKGROUNG } from 'src/colors';
import AutocompleteSearchbarforCountryWithCode from './AutocompleteSearchbarForCountry';

const useStyles = makeStyles((theme: Theme) => ({
  paperModal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '75%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(4, 10, 4),
    borderRadius: 10,
    backgroundColor: '#fff',
    [theme.breakpoints.down(960)]: {
      height: '60%',
      overflow: 'scroll',
      padding: theme.spacing(4, 4, 4),
    },
  },
  paymentBorder: {
    border: `2px solid ${COLOR_GREY}`,
    borderRadius: 10,
    padding: theme.spacing(2, 3, 2),
    boxShadow: theme.shadows[5],
    [theme.breakpoints.down(960)]: {
      marginBottom: 40,
    },
  },
  couponField: {
    marginTop: 10,
    marginBottom: theme.spacing(1),
    width: '100%',
  },
  switchContainer: {
    backgroundColor: COLOR_PRIMARY,
    borderRadius: 50,
    width: 115,
    height: 25,
  },
  switch: {
    borderRadius: 50,
    height: 20,
    width: 55,
    padding: 5,
  },
  switchIcon: {
    color: COLOR_WHITE,
  },
  switchIconActive: {
    color: COLOR_BLACK,
  },
  switchActive: {
    backgroundColor: COLOR_WHITE,
    '&:hover': {
      backgroundColor: COLOR_WHITE,
    },
  },
}));

const getMarketsText = (product: string) => {
  if (product !== 'country') {
    const markets: number = (ProductIDOptions[product] as { markets: number }).markets;
    if (markets > 1) {
      return `${markets} markets`;
    }
    return '1 market';
  }
  return 'Country';
};

const productNameToTitleMap = {
  starter: '1 Market',
  standard: '5 Markets',
};

const PlanSwitchGroup = ({
  values,
  type,
  setType,
  float,
}: {
  values: string[];
  type: string;
  setType: React.Dispatch<React.SetStateAction<string>>;
  float: string;
}) => {
  const buttonStyle: any = {
    background: PAYMENT_MODAL_GREEN,
    borderRadius: 200,
    color: '#000',
    border: 'none !important',
    textTransformation: 'capitalize',
    transition: 'background 0.3s ease',
    '&:hover': {
      background: PAYMENT_MODAL_GREEN_HOVER,
    },
  };
  return (
    <ButtonGroup
      variant="contained"
      size="small"
      sx={{ borderRadius: 200, background: '#fff', float, padding: '2px' }}
    >
      {values.map((data, i) => (
        <Button
          key={i}
          className={data == 'country' ? 'subscriptionModalCountryBtn' : ''}
          size="small"
          onClick={() => {
            setType(data);
          }}
          sx={{
            ...buttonStyle,
            background: type === data ? PAYMENT_MODAL_GREEN : '#fff',
            color: type === data ? '#fff' : '#000',
          }}
        >
          {productNameToTitleMap[data.toLowerCase()]
            ? productNameToTitleMap[data.toLowerCase()]
            : data}
        </Button>
      ))}
    </ButtonGroup>
  );
};

const PlanDropDown = ({
  values,
  type,
  setType,
}: {
  values: string[];
  type: string;
  setType: React.Dispatch<React.SetStateAction<string>>;
}) => {
  return (
    <FormControl fullWidth sx={{ m: 1 }}>
      <Select
        value={type}
        label={productNameToTitleMap[type] ? productNameToTitleMap[type] : type}
        variant="outlined"
        onChange={event => {
          setType(event.target.value);
        }}
        style={{ textTransform: 'capitalize' }}
      >
        {values.map(data => (
          <MenuItem value={data} sx={{ textTransform: 'capitalize' }}>
            {productNameToTitleMap[data.toLowerCase()]
              ? productNameToTitleMap[data.toLowerCase()]
              : data}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const SubscriptionModal = ({
  countryCode,
  city,
  district,
  getTokenThunk,
  subscriptionModalOpen,
  setSubscriptionModalOpen,
  paymentPlanQuery,
  paymentDurationQuery,
  setIsTailoredRegionOpen,
  user,
}: SubscriptionModalPropTypes) => {
  const classes = useStyles();
  const isLoggedIn = Boolean(getTokenThunk);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreenSize = useMediaQuery(theme.breakpoints.down('lg'));

  const [isPromoCodeLoading, setIsPromoCodeLoading] = useState<boolean>(true);
  const [product, setProduct] = useState<string>('standard');
  const [duration, setDuration] = useState<string>('monthly');
  const [promoCode, setPromoCode] = useState<string>('');
  const [discountedPrice, setDiscountedPrice] = useState<null | number>(null);
  const [promoCodeDiscountsDict, setPromoCodeDiscountDict] = useState<any>(null);
  const [countryToUnlock, setCountryToUnlock] = useState<string | null>(null);
  const [countryTier, setCountryTier] = useState<string>('tier3');
  const [defaultCountryToUnlock, setDefaultCountryToUnlock] = useState<any>(null);
  const userCurrentCountryCode = useSelector((state: any) => state.dashboard.userCountryCode);

  //userSignupCountryCode represent the country from where he/she signed up in our app
  const userSignupCountryCode = useSelector((state: any) => state.dashboard.user.signupCountryCode);
  const userLocalCurrency = CountryCodeWithCurrency[userSignupCountryCode] || 'USD';
  const subscriptionPriceAccordingToUserLocalCurrency =
    CurrencyWithSubscriptionPrice[userLocalCurrency];

  const promocodeTextFieldRef = useRef<HTMLInputElement | null>(null);
  const searchedMarketCountryCode = useSelector(
    state => (state as any).dashboard.search.data.countryCode
  );

  const handleCountryToUnlockChange = (countryCode: string) => {
    setCountryToUnlock(countryCode);
    const country_tier = getCountryTier(countryCode);
    setCountryTier(country_tier);

    //the below code is to remove promocode if user switch country from country plan
    if (promoCode) {
      calculateCouponChange(promoCode, countryTier);
    }
  };

  const getCountryTier = countryCode => {
    if (countryCode === 'US' || countryCode === 'FR') {
      return 'tier1';
    } else if (
      countryCode === 'AU' ||
      countryCode === 'CA' ||
      countryCode === 'DE' ||
      countryCode === 'IT' ||
      countryCode === 'JP' ||
      countryCode === 'GB'
    ) {
      return 'tier2';
    } else {
      return 'tier3';
    }
  };

  useEffect(() => {
    if (paymentPlanQuery) {
      setProduct(paymentPlanQuery);
    }
    if (paymentDurationQuery) {
      setDuration(paymentDurationQuery);
    }
  }, [paymentPlanQuery, paymentDurationQuery]);

  useEffect(() => {
    if (promoCode) {
      calculateCouponChange(promoCode, countryTier);
    }
    window.history.pushState({}, '', `?payment-plan=${product}&payment-duration=${duration}`);
  }, [product, duration, countryTier]);

  useEffect(() => {
    const getCoupons = async () => {
      let coupons = await callApi(
        // 'https://airbtics-resource.s3.us-east-2.amazonaws.com/coupons.json',
        'https://airbtics-resource.s3.us-east-2.amazonaws.com/couponsTest.json',
        {},
        false
      );

      //making all coupons capatalize
      for (const coupon in coupons) {
        if (!coupons[coupon.toUpperCase()]) {
          coupons[coupon.toUpperCase()] = coupons[coupon];
          delete coupons[coupon];
        }
      }
      setPromoCodeDiscountDict(coupons);
      setIsPromoCodeLoading(false);
    };
    getCoupons();

    if (searchedMarketCountryCode) {
      setDefaultCountryToUnlock(getCountryDataFromCountryCode(searchedMarketCountryCode));
    } else {
      setDefaultCountryToUnlock(getCountryDataFromCountryCode(userCurrentCountryCode));
    }
  }, []);

  const checkPromocodeValidity = (promocode: string, returnValidPromoCode: boolean = false) => {
    if (!promocode) {
      return returnValidPromoCode ? '' : false;
    }

    const isPromoCodeValid: boolean =
      promoCodeDiscountsDict[promocode] &&
      (!promoCodeDiscountsDict[promocode].domain ||
        promoCodeDiscountsDict[promocode].domain === product);

    if (!returnValidPromoCode) {
      return isPromoCodeValid;
    }

    return isPromoCodeValid ? promocode : '';
  };

  const calculateCouponChange = (currentPromoCode: string, countryTier) => {
    currentPromoCode = currentPromoCode.toUpperCase();
    if (checkPromocodeValidity(currentPromoCode)) {
      let newDiscountedPricing = 0;
      const currentPromoCodeObj = promoCodeDiscountsDict[currentPromoCode];
      const currentPrice = Number(
        product === 'country'
          ? subscriptionPriceAccordingToUserLocalCurrency['country'][countryTier][duration]
          : subscriptionPriceAccordingToUserLocalCurrency[product][duration]
      );

      if (currentPromoCodeObj.type === 'percent') {
        newDiscountedPricing =
          currentPrice -
          currentPrice * (Number(promoCodeDiscountsDict[currentPromoCode].amount) / 100);
      } else if (currentPromoCodeObj.type === 'fixed-off') {
        newDiscountedPricing = currentPrice - Number(currentPromoCodeObj.amount);
      } else if (currentPromoCodeObj.type === 'fixed-price') {
        newDiscountedPricing = Number(currentPromoCodeObj.amount);
      }
      setDiscountedPrice(_ => Number(newDiscountedPricing.toFixed(2)));
    } else {
      setDiscountedPrice(null);
    }
  };

  const getListingsCountCountryPlan = () => {
    const listingCount = countryCodeToNumberOfListingsCountryPlan[
      countryToUnlock?.toLowerCase() + ''
    ]
      ? countryCodeToNumberOfListingsCountryPlan[countryToUnlock?.toLowerCase() + '']
      : countryCodeToNumberOfListingsCountryPlan['other'];
    return formatCurrency(listingCount + '');
  };

  const getProductTextCard = (text: JSX.Element) => {
    return (
      <Grid
        container
        xs={12}
        spacing={1}
        style={{
          width: '100%',
          padding: '4px 6px',
          borderRadius: isMobile ? 10 : 200,
          boxShadow: '0 2px 2px #ccc',
          flexWrap: 'nowrap',
        }}
        alignItems="center"
        justifyContent="flex-start"
      >
        <Grid
          item
          style={{
            background: PAYMENT_MODAL_GREEN,
            borderRadius: 200,
            height: '30px',
            width: '30px',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            stroke: '#fff',
            strokeWidth: '2px',
          }}
        >
          <DoneRoundedIcon sx={{ color: '#fff', fontWeight: 'bold' }} />
        </Grid>
        <Grid item style={{ marginLeft: '5px' }}>
          <Typography variant="body1">{text}</Typography>
        </Grid>
      </Grid>
    );
  };

  const handleCouponChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentPromoCode: string = e.target.value;
    setPromoCode(_ => currentPromoCode);
    calculateCouponChange(currentPromoCode, countryTier);
  };

  const DiscountToolTip = ({
    monthlyPrice,
    yearlyPricePerMonth,
    isMobile,
    discountedPricePerMonth,
  }) => {
    const profitPercentile = discountedPricePerMonth
      ? Math.round(((monthlyPrice - discountedPricePerMonth) / monthlyPrice) * 100)
      : Math.round(((monthlyPrice - yearlyPricePerMonth) / monthlyPrice) * 100);
    return (
      <>
        {
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {!isMobile && (
              <div
                style={{
                  width: '10px',
                  height: '10px',
                  transform: 'rotate(45deg)',
                  right: '-6px',
                  position: 'relative',
                  backgroundColor: COLOR_SUCCESS_BACKGROUNG,
                  marginTop: '2px',
                  boxShadow: '-1px 2px 2px rgba(0, 0, 0, 0.2)',
                }}
              ></div>
            )}
            <div
              style={{
                height: '30px',
                width: '120px',
                justifyContent: 'center',
                display: 'flex',
                alignItems: 'center',
                borderRadius: '5px',
                backgroundColor: COLOR_SUCCESS_BACKGROUNG,
                marginTop: '2px',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              }}
            >
              <Typography style={{ color: COLOR_SUCCESS_1, fontWeight: 'bold' }}>
                {' '}
                {profitPercentile}% Savings{' '}
              </Typography>
            </div>
          </div>
        }
      </>
    );
  };

  const getCTAButtonInsidePolyStyle = (
    top: string,
    side: string,
    sideValue: string,
    rotation: number,
    background: string
  ): any => {
    return {
      width: '100px',
      height: '200px',
      position: 'absolute',
      top: top,
      [side]: sideValue,
      transform: `rotate(${rotation}deg)`,
      background: background,
    };
  };

  const getCountryNameFromCountryCode = countryCode => {
    let countryName;
    countryListWithCode.map(countryData => {
      if (countryData.code === countryCode) {
        countryName = countryData.label;
      }
    });
    return countryName;
  };

  return (
    <Modal
      open={subscriptionModalOpen && isLoggedIn}
      onClose={() => {
        setSubscriptionModalOpen(false);
        window.history.pushState({}, '', window.location.pathname);
      }}
      aria-labelledby="sunscription-modal"
      aria-describedby="subscription-and-payment-details"
    >
      <Grid container direction="column" alignItems="center" justifyContent="center">
        <div
          className={`${classes.paperModal} modal-custom`}
          style={{ height: isMobile ? '85vh' : 'auto' }}
        >
          {isMobile && (
            <div
              style={{
                position: 'absolute',
                right: '2px',
                top: '2px',
                cursor: 'pointer',
              }}
              onClick={() => setSubscriptionModalOpen(false)}
            >
              <CloseRoundedIcon sx={{ fontSize: '40px', color: COLOR_RED }} />
            </div>
          )}

          <Grid container direction="column" alignItems="center" justifyContent="center">
            <Typography
              variant="h4"
              style={{
                marginBottom: 50,
                fontSize: 32,
                fontWeight: 'bolder',
                color: COLOR_BLUE_BLACK_LIGHT,
              }}
            >
              You're just one step away from making{' '}
              <span style={{ color: PAYMENT_MODAL_GREEN }}>data-informed</span> decisions!
            </Typography>

            <Grid container direction="row" alignItems="flex-start" justifyContent="space-between">
              <Grid
                container
                direction="column"
                justifyContent="flex-start"
                alignItems="flex-start"
                item
                lg={6}
                md={6}
                xs={12}
              >
                <Grid
                  container
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  item
                  xs={12}
                  spacing={2}
                  style={{ width: '100%', marginBottom: '20px' }}
                >
                  <Grid item xs={isMediumScreenSize ? 12 : 4}>
                    {isMobile ? (
                      <div>
                        <PlanDropDown
                          values={['monthly', 'yearly']}
                          type={duration}
                          setType={setDuration}
                        />
                        {duration == 'yearly' && (
                          <DiscountToolTip
                            monthlyPrice={
                              product == 'country'
                                ? subscriptionPriceAccordingToUserLocalCurrency['country'][
                                    countryTier
                                  ]['monthly']
                                : subscriptionPriceAccordingToUserLocalCurrency[product]['monthly']
                            }
                            yearlyPricePerMonth={
                              product == 'country'
                                ? subscriptionPriceAccordingToUserLocalCurrency['country'][
                                    countryTier
                                  ]['yearly'] / 12
                                : subscriptionPriceAccordingToUserLocalCurrency[product]['yearly'] /
                                  12
                            }
                            isMobile={true}
                            discountedPricePerMonth={discountedPrice ? discountedPrice / 12 : null}
                          />
                        )}
                      </div>
                    ) : (
                      <div style={{ display: 'flex' }}>
                        <PlanSwitchGroup
                          values={['monthly', 'yearly']}
                          type={duration}
                          setType={setDuration}
                          float={'left'}
                        />
                        {duration == 'yearly' && (
                          <DiscountToolTip
                            monthlyPrice={
                              product == 'country'
                                ? subscriptionPriceAccordingToUserLocalCurrency['country'][
                                    countryTier
                                  ]['monthly']
                                : subscriptionPriceAccordingToUserLocalCurrency[product]['monthly']
                            }
                            yearlyPricePerMonth={
                              product == 'country'
                                ? subscriptionPriceAccordingToUserLocalCurrency['country'][
                                    countryTier
                                  ]['yearly'] / 12
                                : subscriptionPriceAccordingToUserLocalCurrency[product]['yearly'] /
                                  12
                            }
                            isMobile={false}
                            discountedPricePerMonth={discountedPrice ? discountedPrice / 12 : null}
                          />
                        )}
                      </div>
                    )}
                  </Grid>
                  <Grid item xs={isMediumScreenSize ? 12 : 8}>
                    {isMobile ? (
                      <PlanDropDown
                        values={['standard', 'starter', 'country']}
                        type={product}
                        setType={setProduct}
                      />
                    ) : (
                      <PlanSwitchGroup
                        values={['standard', 'starter', 'country']}
                        type={product}
                        setType={setProduct}
                        float={'right'}
                      />
                    )}
                  </Grid>
                </Grid>

                <Grid container xs={12} style={{ marginBottom: '20px' }}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="h5"
                      style={{
                        textTransform: 'capitalize',
                        fontWeight: 'bold',
                        color: COLOR_BLUE_BLACK_LIGHT,
                      }}
                    >
                      {product === 'country'
                        ? 'Country Plan'
                        : productNameToTitleMap[product.toLowerCase()] + ' Plan'}
                    </Typography>
                    <Grid item xs={12} spacing={4}>
                      {product == 'country' && (
                        <AutocompleteSearchbarforCountryWithCode
                          onCountryChange={handleCountryToUnlockChange}
                          defaultCountryToUnlock={defaultCountryToUnlock}
                        />
                      )}
                    </Grid>
                  </Grid>
                  <Grid container item xs={12} sm={6}>
                    <Grid item xs={12}>
                      <Typography
                        variant="h5"
                        style={{
                          float: isMobile ? 'left' : 'right',
                          fontWeight: 'bold',
                          color: '#888',
                          display: 'flex',
                          gap: 1,
                          alignItems: 'flex-end',
                        }}
                      >
                        <span
                          style={{
                            color: PAYMENT_MODAL_GREEN,
                            textDecoration: discountedPrice ? 'line-through' : 'none',
                          }}
                        >
                          {exchangeRates[userLocalCurrency].symbol}
                          {product === 'country'
                            ? formatCurrencyBasedOnCountryCode(
                                subscriptionPriceAccordingToUserLocalCurrency['country'][
                                  countryTier
                                ][duration],
                                userLocalCurrency
                              )
                            : formatCurrencyBasedOnCountryCode(
                                subscriptionPriceAccordingToUserLocalCurrency[product][duration],
                                userLocalCurrency
                              )}
                        </span>{' '}
                        <Typography variant="body1">
                          {duration === 'monthly' ? '/month' : '/year'}
                        </Typography>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} style={{ float: 'right' }}>
                      {discountedPrice && (
                        <Typography
                          variant="h5"
                          style={{
                            float: 'right',
                            fontWeight: 'bold',
                            color: '#888',
                            display: 'flex',
                            gap: 1,
                            alignItems: 'flex-end',
                          }}
                        >
                          <span style={{ color: PAYMENT_MODAL_GREEN }}>
                            {exchangeRates[userLocalCurrency].symbol}
                            {formatCurrencyBasedOnCountryCode(discountedPrice, userLocalCurrency)}
                          </span>{' '}
                          <Typography variant="body1">
                            {duration === 'monthly' ? '/month' : '/year'}
                          </Typography>
                        </Typography>
                      )}
                    </Grid>
                    {duration !== 'monthly' && (
                      <Grid item xs={12}>
                        <Typography
                          variant="h5"
                          style={{
                            float: isMobile ? 'left' : 'right',
                            color: '#888',
                            display: 'flex',
                            gap: 1,
                            alignItems: 'flex-end',
                            fontSize: '15px',
                          }}
                        >
                          <span>
                            {'(' +
                              exchangeRates[userLocalCurrency].symbol +
                              (discountedPrice
                                ? formatCurrencyBasedOnCountryCode(
                                    parseFloat((discountedPrice / 12).toFixed(1)),
                                    userLocalCurrency
                                  )
                                : product === 'country'
                                ? formatCurrencyBasedOnCountryCode(
                                    parseFloat(
                                      (
                                        subscriptionPriceAccordingToUserLocalCurrency['country'][
                                          countryTier
                                        ]['yearly'] / 12
                                      ).toFixed(1)
                                    ),
                                    userLocalCurrency
                                  )
                                : formatCurrencyBasedOnCountryCode(
                                    parseFloat(
                                      (
                                        subscriptionPriceAccordingToUserLocalCurrency[product][
                                          'yearly'
                                        ] / 12
                                      ).toFixed(1)
                                    ),
                                    userLocalCurrency
                                  ))}
                          </span>{' '}
                          <Typography variant="body1">{'/month)'}</Typography>
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>

                <List style={{ paddingRight: 0, marginBottom: 20 }}>
                  <ListItem
                    component="li"
                    style={{ marginBottom: '10px', paddingRight: 0, paddingLeft: 0 }}
                  >
                    {getProductTextCard(
                      <>
                        {product !== 'country' && (
                          <span>
                            Full access to{' '}
                            {product === 'country'
                              ? ProductIDOptions['country'][countryTier].markets
                              : (ProductIDOptions[product] as { markets: number }).markets > 1
                              ? 'dashboards'
                              : 'dashboard'}{' '}
                            of{' '}
                            <span style={{ fontWeight: 'bold' }}>
                              {product === 'country' ? 'entire country' : getMarketsText(product)}{' '}
                              (Insights)
                            </span>
                          </span>
                        )}
                        {product === 'country' && (
                          <span>
                            Full access to the{' '}
                            <span style={{ fontWeight: 'bold' }}>
                              {getCountryNameFromCountryCode(countryToUnlock)}
                            </span>
                            <img
                              loading="lazy"
                              width="20"
                              src={`https://flagcdn.com/w20/${countryToUnlock?.toLowerCase()}.png`}
                              srcSet={`https://flagcdn.com/w40/${countryToUnlock?.toLowerCase()}.png 2x`}
                              alt=""
                            />{' '}
                            (Insights)
                          </span>
                        )}
                        <InfoTooltip
                          title={
                            <Card>
                              <CardContent sx={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                                <div
                                  style={{
                                    fontWeight: 500,
                                    fontFamily: 'Roboto',
                                    fontSize: '1rem',
                                    lineHeight: 1.6,
                                  }}
                                >
                                  A market can be any region or area on the map (ex. city,
                                  neighborhood, or even two adjacent neighborhoods). You can use our
                                  dashboard’s search bar to type the region’s name or{' '}
                                  <Link
                                    style={{
                                      fontWeight: 'bold',
                                      color: COLOR_PRIMARY_DARK,
                                      cursor: 'pointer',
                                    }}
                                    target="_blank"
                                    onClick={() => {
                                      setIsTailoredRegionOpen(true);
                                      setSubscriptionModalOpen(false);
                                      window.history.pushState({}, '', `/tailored/t`);
                                    }}
                                  >
                                    "Draw your search area" (create your own custom market)
                                  </Link>
                                  . The only limitation for creating a custom market is that it must
                                  not contain more than 20,000 Airbnb listings. Unlocking the data
                                  for a searched or custom market will cost 1 market credit.
                                </div>
                              </CardContent>
                            </Card>
                          }
                        >
                          <span
                            style={{
                              background: '#C4E7E5',
                              borderRadius: '15px',
                              cursor: 'pointer',
                              padding: '0 5px',
                              marginLeft: 5,
                              fontWeight: 'bold',
                            }}
                          >
                            {' '}
                            What is a market?
                          </span>
                        </InfoTooltip>
                        .
                      </>
                    )}
                  </ListItem>
                  <ListItem style={{ marginBottom: '10px', paddingRight: 0, paddingLeft: 0 }}>
                    {getProductTextCard(
                      <>
                        12 months of historical performance data of{' '}
                        <span style={{ fontWeight: 'bold' }}>
                          up to{' '}
                          {product !== 'country' &&
                            formatCurrency(
                              (
                                20000 * (ProductIDOptions[product] as { markets: number }).markets
                              ).toString()
                            )}
                          {product === 'country' && getListingsCountCountryPlan()} short-term
                          rentals.
                        </span>
                      </>
                    )}
                  </ListItem>
                  <ListItem style={{ marginBottom: '10px', paddingRight: 0, paddingLeft: 0 }}>
                    {getProductTextCard(
                      <>
                        CSV export, Weekly data updates, Guest demographics, Booking lead time, and
                        <Box display="inline" fontWeight="bold">
                          <InfoTooltip
                            title={
                              <Card>
                                <CardContent sx={{ background: COLOR_TOOLTIP_BACKGROUND }}>
                                  <div
                                    style={{
                                      fontWeight: 500,
                                      fontFamily: 'Roboto',
                                      fontSize: '1rem',
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    Find out how much a property could earn as a short term rental.
                                    Enter any address to view comparable existing Airbnbs,
                                    seasonality, and calculate return on investment.
                                  </div>
                                </CardContent>
                              </Card>
                            }
                          >
                            <span
                              style={{
                                background: '#C4E7E5',
                                borderRadius: '15px',
                                cursor: 'pointer',
                                padding: '0 5px',
                                marginLeft: 5,
                                fontWeight: 'bold',
                              }}
                            >
                              {product == 'country'
                                ? ProductIDOptions['country'][countryTier].estimatorCredits
                                : ProductIDOptions[product].estimatorCredits}{' '}
                              Estimator Credits (Estimator)
                            </span>
                          </InfoTooltip>
                          .
                        </Box>
                      </>
                    )}
                  </ListItem>

                  <ListItem style={{ marginBottom: '10px', paddingRight: 0, paddingLeft: 0 }}>
                    {getProductTextCard(
                      <>
                        Unlimited access to{' '}
                        <Box display="inline" fontWeight="bold">
                          Discovery
                        </Box>{' '}
                        tool.
                      </>
                    )}
                  </ListItem>

                  {product !== 'country' && (
                    <ListItem style={{ marginBottom: '10px', paddingRight: 0, paddingLeft: 0 }}>
                      {getProductTextCard(
                        <>
                          <Box display="inline" fontWeight="bold">
                            15-day money-back guarantee
                          </Box>
                          , if you are not happy with the service.
                        </>
                      )}
                    </ListItem>
                  )}
                </List>
              </Grid>
              <Grid
                container
                item
                direction="column"
                // className={classes.paymentBorder}
                lg={5}
                md={5}
                xs={12}
              >
                <Grid item xs={12} spacing={4} className={classes.paymentBorder}>
                  <Payment
                    countryCode={countryCode}
                    city={city}
                    district={district}
                    product={product}
                    duration={duration}
                    promoCode={checkPromocodeValidity(promoCode?.toUpperCase(), true) as string}
                    setSubscriptionModalOpen={setSubscriptionModalOpen}
                    discountedPrice={discountedPrice}
                    countryToUnlock={countryToUnlock}
                    countryTier={countryTier}
                    subscriptionPriceAccordingToUserLocalCurrency={
                      subscriptionPriceAccordingToUserLocalCurrency
                    }
                    userLocalCurrency={userLocalCurrency}
                  />
                </Grid>
                <Grid item xs={12} style={{ marginTop: '20px' }}>
                  <Grid container alignItems="center">
                    <Grid item xs={isPromoCodeLoading ? 11 : 12}>
                      <TextField
                        placeholder="Promo Code (Optional)"
                        type="text"
                        className={classes.couponField}
                        inputRef={promocodeTextFieldRef}
                        onChange={handleCouponChange}
                        InputProps={
                          !isPromoCodeLoading && promoCode !== ''
                            ? {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {checkPromocodeValidity(promoCode?.toUpperCase()) ? (
                                      <CheckCircleIcon color="success" />
                                    ) : (
                                      <InfoTooltip
                                        title={
                                          <Card>
                                            <CardContent
                                              sx={{ background: COLOR_RED, color: '#fff' }}
                                            >
                                              <Typography variant="body1">
                                                Promo code is invalid.
                                              </Typography>
                                            </CardContent>
                                          </Card>
                                        }
                                      >
                                        <ErrorIcon color="error" sx={{cursor: 'pointer'}} />
                                      </InfoTooltip>
                                    )}
                                  </InputAdornment>
                                ),
                              }
                            : {}
                        }
                      />
                    </Grid>
                    {isPromoCodeLoading && (
                      <Grid item xs={1} alignItems="center" justifyContent="center">
                        <CircularProgress size={20} />
                      </Grid>
                    )}
                  </Grid>
                  <Typography variant="caption" color="textPrimary">
                    {' '}
                    Promo discounts will be applied upon checkout and in your emailed invoice
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Box
              sx={{
                padding: '10px 20px',
                marginTop: isMobile ? '40px' : 'auto',
                boxShadow: '0 4px 4px #ccc',
                width: isMobile ? 'auto' : 'min(max(300px, 80%), 600px)',
                textAlign: 'center',
                borderRadius: 500,
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                transform: isMobile ? 'scale(0.85)' : 'scale(1)',
                transition: 'box-shadow 0.3s ease',
                '&:hover': {
                  boxShadow: '0 6px 16px #888',
                },
              }}
              onClick={() => {
                postOnSlack(
                  'C05JDGPMH8T',
                  `User: ${user.email} clicked on Talk to Sales Button In the Subscription Modal.`
                );
                window.open('https://wll5z0jqe4c.typeform.com/to/t9O80SZ2');
              }}
            >
              <div
                style={getCTAButtonInsidePolyStyle('-50%', 'left', '-57px', -13.42, '#9DD7D4')}
              />
              <div
                style={getCTAButtonInsidePolyStyle(
                  isMobile ? '-160%' : '-250%',
                  'right',
                  '-55px',
                  289,
                  '#C4E7E5'
                )}
              />
              <div style={getCTAButtonInsidePolyStyle('-50%', 'right', '-50px', 40, '#9DD7D4')} />
              <Typography
                variant="h6"
                style={{
                  position: 'relative',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  color: COLOR_BLUE_BLACK_LIGHT,
                  zIndex: 500,
                }}
              >
                Need <span style={{ color: PAYMENT_MODAL_GREEN }}>Custom Data</span> or{' '}
                <span style={{ color: PAYMENT_MODAL_GREEN }}>API</span>? Talk to Sales
              </Typography>
            </Box>
          </Grid>
        </div>
      </Grid>
    </Modal>
  );
};

export default SubscriptionModal;
